import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Box, Button, Typography, Grid, Paper, Tabs, Tab, Switch, FormControlLabel, MenuItem, IconButton, Chip } from '@mui/material';
import { Formik, Form, FieldArray } from 'formik';
import { useDropzone } from 'react-dropzone';
import toast from 'react-hot-toast';
import { ArrowLeft, Save, Palette, Image as ImageIcon, FileText, Share2, Globe, Sliders, UploadCloud, Trash2, Plus, ArrowUp, ArrowDown, BookOpen, Building, Calendar, Users } from 'lucide-react';
import { useSuperAdminSchoolStore } from '@/store/superAdmin/schoolStore';
import { PageHeader } from '@/components/navigation/PageHeader';
import { FormField } from '@/components/forms/FormField';
import { Loader } from '@/components/feedback/Loader';
import { ErrorState } from '@/components/feedback/ErrorState';
import { tokens } from '@/theme/tokens';
import { mockSchoolWebsiteData } from '@/mock/mockSchoolWebsiteData';

const PRESET_COLORS = [
  { name: 'Royal Blue', hex: '#1E5EFF' },
  { name: 'Emerald Green', hex: '#10B981' },
  { name: 'Indigo', hex: '#6366F1' },
  { name: 'Purple', hex: '#8B5CF6' },
  { name: 'Pink', hex: '#EC4899' },
  { name: 'Amber Gold', hex: '#F59E0B' },
  { name: 'Crimson Red', hex: '#EF4444' },
  { name: 'Cyan Blue', hex: '#06B6D4' },
  { name: 'Teal', hex: '#059669' },
  { name: 'Dark Slate', hex: '#111827' },
];

const CURRENCY_OPTIONS = [
  { value: 'INR', label: 'INR — Indian Rupee (₹)' },
  { value: 'USD', label: 'USD — US Dollar ($)' },
  { value: 'EUR', label: 'EUR — Euro (€)' },
  { value: 'GBP', label: 'GBP — British Pound (£)' },
  { value: 'AED', label: 'AED — UAE Dirham' },
  { value: 'SAR', label: 'SAR — Saudi Riyal' },
  { value: 'CAD', label: 'CAD — Canadian Dollar ($)' },
  { value: 'AUD', label: 'AUD — Australian Dollar ($)' },
  { value: 'SGD', label: 'SGD — Singapore Dollar ($)' },
];

const TIMEZONE_OPTIONS = [
  { value: 'Asia/Kolkata', label: 'Asia/Kolkata (IST — GMT+5:30)' },
  { value: 'Asia/Dubai', label: 'Asia/Dubai (GST — GMT+4:00)' },
  { value: 'Asia/Riyadh', label: 'Asia/Riyadh (AST — GMT+3:00)' },
  { value: 'Asia/Singapore', label: 'Asia/Singapore (SGT — GMT+8:00)' },
  { value: 'Asia/Kathmandu', label: 'Asia/Kathmandu (NPT — GMT+5:45)' },
  { value: 'Asia/Dhaka', label: 'Asia/Dhaka (BST — GMT+6:00)' },
  { value: 'Asia/Colombo', label: 'Asia/Colombo (SLST — GMT+5:30)' },
  { value: 'Europe/London', label: 'Europe/London (GMT — GMT+0:00)' },
  { value: 'Europe/Paris', label: 'Europe/Paris (CET — GMT+1:00)' },
  { value: 'America/New_York', label: 'America/New_York (EST — GMT-5:00)' },
  { value: 'America/Chicago', label: 'America/Chicago (CST — GMT-6:00)' },
  { value: 'America/Los_Angeles', label: 'America/Los_Angeles (PST — GMT-8:00)' },
  { value: 'Australia/Sydney', label: 'Australia/Sydney (AEST — GMT+10:00)' },
];

function ColorPickerSection({ label, colorName, hexValue, onChange }) {
  return (
    <Paper
      elevation={0}
      sx={{
        p: 2,
        borderRadius: tokens.radius.sm,
        border: `1px solid ${tokens.colors.border}`,
        bgcolor: tokens.colors.background.default,
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
      }}
    >
      <Box>
        <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1.5, color: tokens.colors.text.primary, display: 'flex', alignItems: 'center', gap: 1 }}>
          <Palette size={16} /> {label}
        </Typography>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
          <Box
            component="input"
            type="color"
            value={hexValue || '#1E5EFF'}
            onChange={(e) => onChange(colorName, e.target.value)}
            sx={{
              width: 44,
              height: 40,
              p: 0.5,
              borderRadius: tokens.radius.sm,
              border: `1px solid ${tokens.colors.border}`,
              cursor: 'pointer',
              bgcolor: tokens.colors.card,
              flexShrink: 0,
            }}
          />
          <Box sx={{ flex: 1, '& > .MuiBox-root': { mb: 0 } }}>
            <FormField
              name={colorName}
              placeholder="#1E5EFF"
              size="small"
              fullWidth
            />
          </Box>
        </Box>

        <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1, fontWeight: 500 }}>
          Quick Color Presets:
        </Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
          {PRESET_COLORS.map((c) => {
            const isSelected = (hexValue || '').toLowerCase() === c.hex.toLowerCase();
            return (
              <Chip
                key={c.hex}
                onClick={() => onChange(colorName, c.hex)}
                avatar={
                  <Box
                    sx={{
                      width: 14,
                      height: 14,
                      borderRadius: '50%',
                      backgroundColor: c.hex,
                      ml: 0.5,
                      border: '1px solid rgba(0,0,0,0.1)',
                    }}
                  />
                }
                label={c.name}
                size="small"
                variant={isSelected ? 'filled' : 'outlined'}
                sx={{
                  fontSize: '0.75rem',
                  borderColor: isSelected ? c.hex : tokens.colors.border,
                  bgcolor: isSelected ? `${c.hex}18` : 'transparent',
                  color: isSelected ? c.hex : tokens.colors.text.primary,
                  fontWeight: isSelected ? 600 : 400,
                  cursor: 'pointer',
                  '&:hover': {
                    bgcolor: `${c.hex}25`,
                  },
                }}
              />
            );
          })}
        </Box>
      </Box>
    </Paper>
  );
}

function FileDropzone({ label, file, fileUrl, onFileSelect, onFileRemove }) {
  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    accept: { 'image/*': ['.png', '.jpg', '.jpeg', '.webp', '.ico', '.svg'] },
    multiple: false,
    onDrop: (acceptedFiles) => {
      if (acceptedFiles && acceptedFiles.length > 0) {
        onFileSelect(acceptedFiles[0]);
      }
    },
  });

  const [previewUrl, setPreviewUrl] = useState(null);

  useEffect(() => {
    if (file) {
      const objectUrl = URL.createObjectURL(file);
      setPreviewUrl(objectUrl);
      return () => URL.revokeObjectURL(objectUrl);
    } else if (fileUrl && typeof fileUrl === 'string' && fileUrl.trim().length > 0) {
      setPreviewUrl(fileUrl);
    } else {
      setPreviewUrl(null);
    }
  }, [file, fileUrl]);

  return (
    <Box sx={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Typography variant="caption" fontWeight={600} color="text.secondary" display="block" sx={{ mb: 0.75 }}>
        {label}
      </Typography>

      {previewUrl ? (
        <Paper
          elevation={0}
          sx={{
            p: 1.5,
            border: `1px solid ${tokens.colors.border}`,
            borderRadius: tokens.radius.sm,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            bgcolor: tokens.colors.grey[50],
            minHeight: 88,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, overflow: 'hidden' }}>
            <Box
              component="img"
              src={previewUrl}
              alt={label}
              sx={{
                width: 48,
                height: 48,
                borderRadius: tokens.radius.xs || 1,
                objectFit: 'contain',
                bgcolor: '#ffffff',
                p: 0.5,
                border: `1px solid ${tokens.colors.border}`,
                flexShrink: 0,
              }}
              onError={(e) => { e.target.style.display = 'none'; }}
            />
            <Box sx={{ overflow: 'hidden' }}>
              <Typography variant="body2" fontWeight={600} noWrap sx={{ fontSize: '0.82rem' }}>
                {file ? file.name : (typeof fileUrl === 'string' ? fileUrl.split('/').pop() : 'Uploaded Asset')}
              </Typography>
              <Typography variant="caption" color="success.main" fontWeight={600} display="block">
                {file ? `${(file.size / 1024).toFixed(1)} KB (Ready)` : 'Active Asset'}
              </Typography>
            </Box>
          </Box>
          <IconButton size="small" color="error" onClick={onFileRemove}>
            <Trash2 size={16} />
          </IconButton>
        </Paper>
      ) : (
        <Paper
          {...getRootProps()}
          elevation={0}
          sx={{
            p: 2,
            minHeight: 88,
            border: `2px dashed ${isDragActive ? tokens.colors.primary.main : tokens.colors.border}`,
            borderRadius: tokens.radius.sm,
            bgcolor: isDragActive ? `${tokens.colors.primary.main}08` : tokens.colors.background.default,
            cursor: 'pointer',
            textAlign: 'center',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            transition: 'all 0.2s ease',
            '&:hover': {
              borderColor: tokens.colors.primary.main,
              bgcolor: `${tokens.colors.primary.main}05`,
            },
          }}
        >
          <input {...getInputProps()} />
          <UploadCloud size={24} color={isDragActive ? tokens.colors.primary.main : tokens.colors.text.secondary} />
          <Typography variant="caption" fontWeight={600} sx={{ mt: 0.5, color: tokens.colors.text.primary }}>
            {isDragActive ? 'Drop image here' : 'Drag & drop image here'}
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
            or click to browse
          </Typography>
        </Paper>
      )}
    </Box>
  );
}

export function GlobalSchoolConfigurationPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { 
    selectedSchool: school, 
    schoolConfiguration: fullConfig,
    loading, 
    error, 
    fetchSchoolById, 
    fetchSchoolConfiguration,
    saveSchoolConfiguration 
  } = useSuperAdminSchoolStore();

  const [activeTab, setActiveTab] = useState(0);

  useEffect(() => {
    if (id) {
      fetchSchoolById(id);
      fetchSchoolConfiguration(id);
    }
  }, [id, fetchSchoolById, fetchSchoolConfiguration]);

  if (loading && !school) return <Loader message="Loading school details..." />;
  if (error) return <ErrorState message={error} onRetry={() => { fetchSchoolById(id); fetchSchoolConfiguration(id); }} />;
  if (!school) return <ErrorState message="School not found" onRetry={() => fetchSchoolById(id)} />;

  const domain = fullConfig?.domain || school?.domain || {};
  const settings = fullConfig?.settings || school?.settings || school?.configuration || {};
  const website = fullConfig?.website || school?.website || {};

  const isConfigured = !!(domain?.id || settings?.id || website?.id || fullConfig?.domain?.id || fullConfig?.settings?.id);

  // Read existing content if it exists, otherwise fallback to mock data
  let existingContent = {};
  if (website?.content) {
    if (typeof website.content === 'string') {
      try {
        existingContent = JSON.parse(website.content);
      } catch (e) {
        console.warn("Failed to parse existing content JSON string", e);
      }
    } else if (typeof website.content === 'object') {
      existingContent = website.content;
    }
  }

  const getArr = (key) => existingContent[key] || [];
  const getObj = (key) => existingContent[key] || null;
  const getStr = (key) => existingContent[key] || '';

  const configInitialValues = {
    subdomain: domain.hostname ? domain.hostname.split('.')[0] : (domain.subdomain || ''),
    website_enabled: settings.website_enabled ?? website.website_enabled ?? true,
    erp_enabled: settings.erp_enabled ?? true,
    template: website.template || 'default',
    primary_color: settings.primary_color || '#1e3a8a',
    secondary_color: settings.secondary_color || '#0284c7',
    logo: settings.logo_url || school.logo || '',
    favicon: settings.favicon_url || '',
    login_image: settings.login_image_url || '',
    logoFile: null,
    faviconFile: null,
    login_imageFile: null,
    currency: settings.currency || school.currency || 'INR',
    timezone: settings.timezone || school.timezone || 'Asia/Kolkata',

    school_name: getStr('school_name') || school?.school_name || school?.name || '',
    tagline: getStr('tagline'),
    
    hero: getObj('hero') || { title: '', subtitle: '', image: '', cta_primary: '', cta_secondary: '' },
    quick_info: getObj('quick_info') || { email: school?.email || '', phone: school?.phone || '', address: school?.address || '', working_hours: '' },
    about: getObj('about') || { title: '', content: '', image: '' },
    principal: getObj('principal') || { name: '', title: '', message: '', image: '' },
    
    school_highlights: getArr('school_highlights'),
    academics: getArr('academics'),
    facilities: getArr('facilities'),
    why_choose_us: getArr('why_choose_us'),
    statistics: getArr('statistics'),
    gallery: getArr('gallery'),
    video_gallery: getArr('video_gallery'),
    events: getArr('events'),
    news: getArr('news'),
    testimonials: getArr('testimonials'),
    faculty: getArr('faculty'),
    admission_process: getArr('admission_process'),
    
    cta: getObj('cta') || { title: '', subtitle: '', button_text: '', button_link: '' },
    contact: getObj('contact') || { title: '', subtitle: '', email: school?.email || '', phone: school?.phone || '', address: school?.address || '', map_url: '' },
    social_media: getObj('social_media') || { facebook: '', twitter: '', instagram: '', linkedin: '', youtube: '' },
  };

  const handleFillDemoData = (setValues, currentValues) => {
    setValues({
      ...currentValues,
      school_name: mockSchoolWebsiteData.school_name,
      tagline: mockSchoolWebsiteData.tagline,
      hero: {
        title: mockSchoolWebsiteData.hero_title,
        subtitle: mockSchoolWebsiteData.hero_subtitle,
        image: mockSchoolWebsiteData.hero_image,
        cta_primary: mockSchoolWebsiteData.hero_cta_primary,
        cta_secondary: mockSchoolWebsiteData.hero_cta_secondary,
      },
      quick_info: mockSchoolWebsiteData.quick_info,
      about: {
        title: mockSchoolWebsiteData.about_title,
        content: mockSchoolWebsiteData.about_content,
        image: mockSchoolWebsiteData.about_image,
      },
      principal: {
        name: mockSchoolWebsiteData.principal_name,
        title: mockSchoolWebsiteData.principal_title,
        message: mockSchoolWebsiteData.principal_message,
        image: mockSchoolWebsiteData.principal_image,
      },
      school_highlights: mockSchoolWebsiteData.school_highlights || [],
      academics: mockSchoolWebsiteData.academics || [],
      facilities: mockSchoolWebsiteData.facilities || [],
      why_choose_us: mockSchoolWebsiteData.why_choose_us || [],
      statistics: mockSchoolWebsiteData.statistics || [],
      gallery: mockSchoolWebsiteData.gallery || [],
      video_gallery: mockSchoolWebsiteData.video_gallery || [],
      events: mockSchoolWebsiteData.events || [],
      news: mockSchoolWebsiteData.news || [],
      testimonials: mockSchoolWebsiteData.testimonials || [],
      faculty: mockSchoolWebsiteData.faculty || [],
      admission_process: mockSchoolWebsiteData.admission_process || [],
      cta: mockSchoolWebsiteData.cta,
      contact: mockSchoolWebsiteData.contact,
      social_media: mockSchoolWebsiteData.social_media,
    });
    toast.success('Demo data loaded! Please review and save.');
  };

  const handleConfigSubmit = async (values, { setSubmitting }) => {
    try {
      const formData = new FormData();
      formData.append('subdomain', values.subdomain || '');
      formData.append('website_enabled', values.website_enabled);
      formData.append('erp_enabled', values.erp_enabled);
      formData.append('template', values.template || 'default');
      formData.append('primary_color', values.primary_color || '#1e3a8a');
      formData.append('secondary_color', values.secondary_color || '#0284c7');
      formData.append('currency', values.currency || 'INR');
      formData.append('timezone', values.timezone || 'Asia/Kolkata');

      if (values.logoFile instanceof File) {
        formData.append('logo', values.logoFile);
      } else {
        formData.append('logo', '');
      }

      if (values.faviconFile instanceof File) {
        formData.append('favicon', values.faviconFile);
      } else {
        formData.append('favicon', '');
      }

      if (values.login_imageFile instanceof File) {
        formData.append('login_image', values.login_imageFile);
      } else {
        formData.append('login_image', '');
      }

      // Build the content object
      const contentObject = {
        school_name: values.school_name,
        tagline: values.tagline,
        hero: values.hero,
        quick_info: values.quick_info,
        about: values.about,
        principal: values.principal,
        school_highlights: values.school_highlights,
        academics: values.academics,
        facilities: values.facilities,
        why_choose_us: values.why_choose_us,
        statistics: values.statistics,
        gallery: values.gallery,
        video_gallery: values.video_gallery,
        events: values.events,
        news: values.news,
        testimonials: values.testimonials,
        faculty: values.faculty,
        admission_process: values.admission_process,
        cta: values.cta,
        contact: values.contact,
        social_media: values.social_media,
      };

      formData.append('content', JSON.stringify(contentObject));

      // Use PUT if already configured (isConfigured is true), else use POST
      await saveSchoolConfiguration(id, formData, isConfigured);
      toast.success(`School configuration ${isConfigured ? 'updated' : 'created'} successfully!`);
    } catch (err) {
      toast.error(err.message || `Failed to ${isConfigured ? 'update' : 'create'} school configuration`);
    } finally {
      setSubmitting(false);
    }
  };

  const ArrayControls = ({ index, remove, swap, length }) => (
    <Box sx={{ display: 'flex', gap: 1 }}>
      <IconButton size="small" onClick={() => swap(index, index - 1)} disabled={index === 0}>
        <ArrowUp size={16} />
      </IconButton>
      <IconButton size="small" onClick={() => swap(index, index + 1)} disabled={index === length - 1}>
        <ArrowDown size={16} />
      </IconButton>
      <IconButton size="small" color="error" onClick={() => remove(index)}>
        <Trash2 size={16} />
      </IconButton>
    </Box>
  );

  return (
    <Box sx={{ pb: 4 }}>
      <PageHeader
        title={`${school.school_name || school.name} Configuration`}
        subtitle="Manage the school's ERP and Website settings."
        actions={
          <Button
            variant="outlined"
            startIcon={<ArrowLeft size={18} />}
            onClick={() => navigate(`/super-admin/schools`)}
            sx={{
              borderRadius: tokens.radius.sm,
              fontWeight: 600,
              textTransform: 'none',
              borderColor: tokens.colors.border,
              color: tokens.colors.text.primary,
            }}
          >
            Back to Schools
          </Button>
        }
      />

      <Formik
        initialValues={configInitialValues}
        onSubmit={handleConfigSubmit}
        enableReinitialize
      >
        {({ values, setFieldValue, setValues, isSubmitting }) => (
          <Form autoComplete="off">
            <Paper elevation={0} sx={{ borderRadius: tokens.radius.md, border: `1px solid ${tokens.colors.border}`, overflow: 'hidden' }}>
              <Box sx={{ borderBottom: 1, borderColor: 'divider', bgcolor: tokens.colors.grey[50], overflowX: 'auto' }}>
                <Tabs
                  value={activeTab}
                  onChange={(e, val) => setActiveTab(val)}
                  sx={{ px: 2, minWidth: 'max-content' }}
                  variant="scrollable"
                  scrollButtons="auto"
                >
                  <Tab label="General" icon={<Sliders size={18} />} iconPosition="start" sx={{ minHeight: 64 }} />
                  <Tab label="Branding" icon={<Palette size={18} />} iconPosition="start" sx={{ minHeight: 64 }} />
                  <Tab label="Content" icon={<FileText size={18} />} iconPosition="start" sx={{ minHeight: 64 }} />
                  <Tab label="Academics" icon={<BookOpen size={18} />} iconPosition="start" sx={{ minHeight: 64 }} />
                  <Tab label="Facilities" icon={<Building size={18} />} iconPosition="start" sx={{ minHeight: 64 }} />
                  <Tab label="Events & News" icon={<Calendar size={18} />} iconPosition="start" sx={{ minHeight: 64 }} />
                  <Tab label="Community" icon={<Users size={18} />} iconPosition="start" sx={{ minHeight: 64 }} />
                  <Tab label="Contact" icon={<Share2 size={18} />} iconPosition="start" sx={{ minHeight: 64 }} />
                </Tabs>
              </Box>

              <Box sx={{ p: 3, minHeight: 400 }}>
                {/* Tab 0: General & Features */}
                {activeTab === 0 && (
                  <Grid container spacing={3}>
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <FormField name="subdomain" label="Subdomain Prefix" placeholder="e.g. hera-public-school" helperText={`https://${values.subdomain || 'subdomain'}.example.com`} />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <FormField name="template" label="Portal Template" select>
                        <MenuItem value="default">Default Template</MenuItem>
                        <MenuItem value="modern-school">Modern School Template</MenuItem>
                        <MenuItem value="classic-school">Classic School Template</MenuItem>
                        <MenuItem value="minimal-school">Minimalist School Template</MenuItem>
                      </FormField>
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <FormField name="currency" label="Currency" select>
                        {CURRENCY_OPTIONS.map((c) => (
                          <MenuItem key={c.value} value={c.value}>
                            {c.label}
                          </MenuItem>
                        ))}
                      </FormField>
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <FormField name="timezone" label="Timezone" select>
                        {TIMEZONE_OPTIONS.map((tz) => (
                          <MenuItem key={tz.value} value={tz.value}>
                            {tz.label}
                          </MenuItem>
                        ))}
                      </FormField>
                    </Grid>

                    <Grid size={{ xs: 12, sm: 6 }}>
                      <Paper elevation={0} sx={{ p: 1.5, border: `1px solid ${tokens.colors.border}`, borderRadius: tokens.radius.sm, bgcolor: tokens.colors.background.default, display: 'flex', alignItems: 'center', minHeight: 48 }}>
                        <FormControlLabel
                          control={
                            <Switch
                              checked={values.website_enabled}
                              onChange={(e) => setFieldValue('website_enabled', e.target.checked)}
                              color="primary"
                            />
                          }
                          label={<Typography variant="body2" fontWeight={600}>Enable Public Website</Typography>}
                          sx={{ m: 0 }}
                        />
                      </Paper>
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <Paper elevation={0} sx={{ p: 1.5, border: `1px solid ${tokens.colors.border}`, borderRadius: tokens.radius.sm, bgcolor: tokens.colors.background.default, display: 'flex', alignItems: 'center', minHeight: 48 }}>
                        <FormControlLabel
                          control={
                            <Switch
                              checked={values.erp_enabled}
                              onChange={(e) => setFieldValue('erp_enabled', e.target.checked)}
                              color="primary"
                            />
                          }
                          label={<Typography variant="body2" fontWeight={600}>Enable School ERP Portal</Typography>}
                          sx={{ m: 0 }}
                        />
                      </Paper>
                    </Grid>
                  </Grid>
                )}

                {/* Tab 1: Branding & Theme */}
                {activeTab === 1 && (
                  <Grid container spacing={3}>
                    <Grid size={{ xs: 12, md: 6 }}>
                      <ColorPickerSection
                        label="Primary Theme Color"
                        colorName="primary_color"
                        hexValue={values.primary_color}
                        onChange={setFieldValue}
                      />
                    </Grid>

                    <Grid size={{ xs: 12, md: 6 }}>
                      <ColorPickerSection
                        label="Secondary Accent Color"
                        colorName="secondary_color"
                        hexValue={values.secondary_color}
                        onChange={setFieldValue}
                      />
                    </Grid>

                    {/* Live Theme Preview Card */}
                    <Grid size={{ xs: 12 }}>
                      <Paper
                        elevation={0}
                        sx={{
                          p: 2,
                          borderRadius: tokens.radius.sm,
                          border: `1px solid ${tokens.colors.border}`,
                          bgcolor: tokens.colors.grey[50],
                          mb: 1,
                        }}
                      >
                        <Typography variant="caption" color="text.secondary" fontWeight={600} display="block" sx={{ mb: 1, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                          Live Theme Preview
                        </Typography>
                        <Box sx={{ p: 2, borderRadius: tokens.radius.sm, bgcolor: '#ffffff', border: `1px solid ${tokens.colors.border}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1.5 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                            <Box sx={{ px: 2.5, py: 1, borderRadius: tokens.radius.sm, bgcolor: values.primary_color || '#1E5EFF', color: '#ffffff', fontWeight: 600, fontSize: '0.85rem' }}>
                              Primary Header
                            </Box>
                            <Box sx={{ px: 2, py: 0.75, borderRadius: tokens.radius.sm, bgcolor: values.secondary_color || '#10B981', color: '#ffffff', fontWeight: 600, fontSize: '0.75rem' }}>
                              Active Badge
                            </Box>
                          </Box>
                          <Typography variant="caption" sx={{ color: values.primary_color || '#1E5EFF', fontWeight: 700 }}>
                            {values.primary_color || '#1E5EFF'} &bull; {values.secondary_color || '#10B981'}
                          </Typography>
                        </Box>
                      </Paper>
                    </Grid>

                    <Grid size={{ xs: 12 }}>
                      <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 0, mt: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                        <ImageIcon size={16} /> Logo & Media Assets (Drag & Drop Upload)
                      </Typography>
                    </Grid>

                    <Grid size={{ xs: 12, md: 4 }}>
                      <FileDropzone
                        label="School Logo"
                        file={values.logoFile}
                        fileUrl={values.logo}
                        onFileSelect={(file) => setFieldValue('logoFile', file)}
                        onFileRemove={() => {
                          setFieldValue('logoFile', null);
                          setFieldValue('logo', '');
                        }}
                      />
                    </Grid>

                    <Grid size={{ xs: 12, md: 4 }}>
                      <FileDropzone
                        label="Favicon Icon"
                        file={values.faviconFile}
                        fileUrl={values.favicon}
                        onFileSelect={(file) => setFieldValue('faviconFile', file)}
                        onFileRemove={() => {
                          setFieldValue('faviconFile', null);
                          setFieldValue('favicon', '');
                        }}
                      />
                    </Grid>

                    <Grid size={{ xs: 12, md: 4 }}>
                      <FileDropzone
                        label="Login Banner Image"
                        file={values.login_imageFile}
                        fileUrl={values.login_image}
                        onFileSelect={(file) => setFieldValue('login_imageFile', file)}
                        onFileRemove={() => {
                          setFieldValue('login_imageFile', null);
                          setFieldValue('login_image', '');
                        }}
                      />
                    </Grid>
                  </Grid>
                )}

                {/* Tab 2: Portal Content */}
                {activeTab === 2 && (
                  <Grid container spacing={3}>
                    <Grid size={{ xs: 12 }}><Typography variant="h6">Basic Info</Typography></Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <FormField name="school_name" label="School Name" />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <FormField name="tagline" label="Tagline" />
                    </Grid>

                    <Grid size={{ xs: 12 }}><Typography variant="h6" sx={{ mt: 2 }}>Hero Section</Typography></Grid>
                    <Grid size={{ xs: 12 }}>
                      <FormField name="hero.title" label="Hero Title" />
                    </Grid>
                    <Grid size={{ xs: 12 }}>
                      <FormField name="hero.subtitle" label="Hero Subtitle" multiline rows={2} />
                    </Grid>
                    <Grid size={{ xs: 12 }}>
                      <FormField name="hero.image" label="Hero Image URL" />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <FormField name="hero.cta_primary" label="Primary CTA Text" />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <FormField name="hero.cta_secondary" label="Secondary CTA Text" />
                    </Grid>

                    <Grid size={{ xs: 12 }}><Typography variant="h6" sx={{ mt: 2 }}>Quick Info</Typography></Grid>
                    <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                      <FormField name="quick_info.established" label="Established Year" />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                      <FormField name="quick_info.affiliation" label="Affiliation" />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                      <FormField name="quick_info.classes" label="Classes" />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                      <FormField name="quick_info.medium" label="Medium" />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                      <FormField name="quick_info.school_type" label="School Type" />
                    </Grid>

                    <Grid size={{ xs: 12 }}><Typography variant="h6" sx={{ mt: 2 }}>About School</Typography></Grid>
                    <Grid size={{ xs: 12 }}>
                      <FormField name="about.title" label="About Title" />
                    </Grid>
                    <Grid size={{ xs: 12 }}>
                      <FormField name="about.content" label="About Content" multiline rows={4} />
                    </Grid>
                    <Grid size={{ xs: 12 }}>
                      <FormField name="about.image" label="About Image URL" />
                    </Grid>

                    <Grid size={{ xs: 12 }}><Typography variant="h6" sx={{ mt: 2 }}>Principal Message</Typography></Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <FormField name="principal.name" label="Principal Name" />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <FormField name="principal.title" label="Principal Title" />
                    </Grid>
                    <Grid size={{ xs: 12 }}>
                      <FormField name="principal.message" label="Message" multiline rows={4} />
                    </Grid>
                    <Grid size={{ xs: 12 }}>
                      <FormField name="principal.image" label="Principal Image URL" />
                    </Grid>
                    
                    <Grid size={{ xs: 12 }}><Typography variant="h6" sx={{ mt: 2 }}>Call to Action (CTA)</Typography></Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <FormField name="cta.title" label="CTA Title" />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <FormField name="cta.subtitle" label="CTA Subtitle" />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <FormField name="cta.button_text" label="Button Text" />
                    </Grid>
                  </Grid>
                )}

                {/* Tab 3: Academics & Highlights */}
                {activeTab === 3 && (
                  <Box>
                    <Typography variant="h6" sx={{ mb: 2 }}>School Highlights</Typography>
                    <FieldArray name="school_highlights">
                      {({ push, remove, swap }) => (
                        <Box sx={{ mb: 4 }}>
                          {values.school_highlights.map((_, index) => (
                            <Paper key={index} sx={{ p: 2, mb: 2, border: `1px solid ${tokens.colors.border}`, display: 'flex', flexDirection: 'column', gap: 2 }}>
                              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <Typography variant="subtitle2" fontWeight="bold">Highlight #{index + 1}</Typography>
                                <ArrayControls index={index} remove={remove} swap={swap} length={values.school_highlights.length} />
                              </Box>
                              <Grid container spacing={2}>
                                <Grid size={{ xs: 12, sm: 6 }}>
                                  <FormField name={`school_highlights.${index}.title`} label="Title" />
                                </Grid>
                                <Grid size={{ xs: 12, sm: 6 }}>
                                  <FormField name={`school_highlights.${index}.icon`} label="Icon Name" helperText="Lucide icon name (e.g. Atom, Trophy)" />
                                </Grid>
                                <Grid size={{ xs: 12 }}>
                                  <FormField name={`school_highlights.${index}.description`} label="Description" multiline rows={2} />
                                </Grid>
                              </Grid>
                            </Paper>
                          ))}
                          <Button startIcon={<Plus size={16} />} onClick={() => push({ title: '', description: '', icon: '' })} variant="outlined">Add Highlight</Button>
                        </Box>
                      )}
                    </FieldArray>

                    <Typography variant="h6" sx={{ mb: 2, mt: 4 }}>Academics</Typography>
                    <FieldArray name="academics">
                      {({ push, remove, swap }) => (
                        <Box sx={{ mb: 4 }}>
                          {values.academics.map((_, index) => (
                            <Paper key={index} sx={{ p: 2, mb: 2, border: `1px solid ${tokens.colors.border}`, display: 'flex', flexDirection: 'column', gap: 2 }}>
                              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <Typography variant="subtitle2" fontWeight="bold">Academic Level #{index + 1}</Typography>
                                <ArrayControls index={index} remove={remove} swap={swap} length={values.academics.length} />
                              </Box>
                              <Grid container spacing={2}>
                                <Grid size={{ xs: 12, sm: 4 }}>
                                  <FormField name={`academics.${index}.id`} label="ID (e.g., primary)" />
                                </Grid>
                                <Grid size={{ xs: 12, sm: 4 }}>
                                  <FormField name={`academics.${index}.title`} label="Title" />
                                </Grid>
                                <Grid size={{ xs: 12, sm: 4 }}>
                                  <FormField name={`academics.${index}.grades`} label="Grades" />
                                </Grid>
                                <Grid size={{ xs: 12 }}>
                                  <FormField name={`academics.${index}.description`} label="Description" multiline rows={2} />
                                </Grid>
                              </Grid>
                            </Paper>
                          ))}
                          <Button startIcon={<Plus size={16} />} onClick={() => push({ id: '', title: '', grades: '', description: '', features: [] })} variant="outlined">Add Academic Level</Button>
                        </Box>
                      )}
                    </FieldArray>

                    <Typography variant="h6" sx={{ mb: 2, mt: 4 }}>Why Choose Us</Typography>
                    <FieldArray name="why_choose_us">
                      {({ push, remove, swap }) => (
                        <Box sx={{ mb: 4 }}>
                          {values.why_choose_us.map((_, index) => (
                            <Paper key={index} sx={{ p: 2, mb: 2, border: `1px solid ${tokens.colors.border}`, display: 'flex', flexDirection: 'column', gap: 2 }}>
                              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <Typography variant="subtitle2" fontWeight="bold">Reason #{index + 1}</Typography>
                                <ArrayControls index={index} remove={remove} swap={swap} length={values.why_choose_us.length} />
                              </Box>
                              <Grid container spacing={2}>
                                <Grid size={{ xs: 12, sm: 6 }}>
                                  <FormField name={`why_choose_us.${index}.title`} label="Title" />
                                </Grid>
                                <Grid size={{ xs: 12, sm: 6 }}>
                                  <FormField name={`why_choose_us.${index}.icon`} label="Icon Name" />
                                </Grid>
                                <Grid size={{ xs: 12 }}>
                                  <FormField name={`why_choose_us.${index}.description`} label="Description" multiline rows={2} />
                                </Grid>
                              </Grid>
                            </Paper>
                          ))}
                          <Button startIcon={<Plus size={16} />} onClick={() => push({ title: '', description: '', icon: '' })} variant="outlined">Add Reason</Button>
                        </Box>
                      )}
                    </FieldArray>
                    
                    <Typography variant="h6" sx={{ mb: 2, mt: 4 }}>Statistics</Typography>
                    <FieldArray name="statistics">
                      {({ push, remove, swap }) => (
                        <Box sx={{ mb: 4 }}>
                          {values.statistics.map((_, index) => (
                            <Paper key={index} sx={{ p: 2, mb: 2, border: `1px solid ${tokens.colors.border}`, display: 'flex', gap: 2, alignItems: 'flex-start' }}>
                              <Box sx={{ flex: 1 }}>
                                <Grid container spacing={2}>
                                  <Grid size={{ xs: 12, sm: 6 }}>
                                    <FormField name={`statistics.${index}.label`} label="Label (e.g. Students Enrolled)" />
                                  </Grid>
                                  <Grid size={{ xs: 12, sm: 6 }}>
                                    <FormField name={`statistics.${index}.value`} label="Value (e.g. 2,500+)" />
                                  </Grid>
                                </Grid>
                              </Box>
                              <ArrayControls index={index} remove={remove} swap={swap} length={values.statistics.length} />
                            </Paper>
                          ))}
                          <Button startIcon={<Plus size={16} />} onClick={() => push({ label: '', value: '' })} variant="outlined">Add Statistic</Button>
                        </Box>
                      )}
                    </FieldArray>
                  </Box>
                )}

                {/* Tab 4: Campus & Facilities */}
                {activeTab === 4 && (
                  <Box>
                    <Typography variant="h6" sx={{ mb: 2 }}>Facilities</Typography>
                    <FieldArray name="facilities">
                      {({ push, remove, swap }) => (
                        <Box sx={{ mb: 4 }}>
                          {values.facilities.map((_, index) => (
                            <Paper key={index} sx={{ p: 2, mb: 2, border: `1px solid ${tokens.colors.border}`, display: 'flex', flexDirection: 'column', gap: 2 }}>
                              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <Typography variant="subtitle2" fontWeight="bold">Facility #{index + 1}</Typography>
                                <ArrayControls index={index} remove={remove} swap={swap} length={values.facilities.length} />
                              </Box>
                              <Grid container spacing={2}>
                                <Grid size={{ xs: 12, sm: 6 }}>
                                  <FormField name={`facilities.${index}.name`} label="Name" />
                                </Grid>
                                <Grid size={{ xs: 12, sm: 6 }}>
                                  <FormField name={`facilities.${index}.icon`} label="Icon Name" />
                                </Grid>
                                <Grid size={{ xs: 12 }}>
                                  <FormField name={`facilities.${index}.description`} label="Description" multiline rows={2} />
                                </Grid>
                              </Grid>
                            </Paper>
                          ))}
                          <Button startIcon={<Plus size={16} />} onClick={() => push({ name: '', description: '', icon: '' })} variant="outlined">Add Facility</Button>
                        </Box>
                      )}
                    </FieldArray>

                    <Typography variant="h6" sx={{ mb: 2, mt: 4 }}>Image Gallery</Typography>
                    <FieldArray name="gallery">
                      {({ push, remove, swap }) => (
                        <Box sx={{ mb: 4 }}>
                          {values.gallery.map((_, index) => (
                            <Paper key={index} sx={{ p: 2, mb: 2, border: `1px solid ${tokens.colors.border}`, display: 'flex', flexDirection: 'column', gap: 2 }}>
                              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <Typography variant="subtitle2" fontWeight="bold">Image #{index + 1}</Typography>
                                <ArrayControls index={index} remove={remove} swap={swap} length={values.gallery.length} />
                              </Box>
                              <Grid container spacing={2}>
                                <Grid size={{ xs: 12, sm: 6 }}>
                                  <FormField name={`gallery.${index}.title`} label="Title" />
                                </Grid>
                                <Grid size={{ xs: 12, sm: 6 }}>
                                  <FormField name={`gallery.${index}.category`} label="Category" />
                                </Grid>
                                <Grid size={{ xs: 12 }}>
                                  <FormField name={`gallery.${index}.image`} label="Image URL" />
                                </Grid>
                              </Grid>
                            </Paper>
                          ))}
                          <Button startIcon={<Plus size={16} />} onClick={() => push({ id: Date.now(), title: '', category: '', image: '' })} variant="outlined">Add Image</Button>
                        </Box>
                      )}
                    </FieldArray>

                    <Typography variant="h6" sx={{ mb: 2, mt: 4 }}>Video Gallery</Typography>
                    <FieldArray name="video_gallery">
                      {({ push, remove, swap }) => (
                        <Box sx={{ mb: 4 }}>
                          {values.video_gallery.map((_, index) => (
                            <Paper key={index} sx={{ p: 2, mb: 2, border: `1px solid ${tokens.colors.border}`, display: 'flex', flexDirection: 'column', gap: 2 }}>
                              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <Typography variant="subtitle2" fontWeight="bold">Video #{index + 1}</Typography>
                                <ArrayControls index={index} remove={remove} swap={swap} length={values.video_gallery.length} />
                              </Box>
                              <Grid container spacing={2}>
                                <Grid size={{ xs: 12, sm: 6 }}>
                                  <FormField name={`video_gallery.${index}.title`} label="Title" />
                                </Grid>
                                <Grid size={{ xs: 12, sm: 6 }}>
                                  <FormField name={`video_gallery.${index}.duration`} label="Duration (e.g. 3:45)" />
                                </Grid>
                                <Grid size={{ xs: 12 }}>
                                  <FormField name={`video_gallery.${index}.thumbnail`} label="Thumbnail URL" />
                                </Grid>
                                <Grid size={{ xs: 12 }}>
                                  <FormField name={`video_gallery.${index}.youtube_url`} label="YouTube Embed URL" />
                                </Grid>
                              </Grid>
                            </Paper>
                          ))}
                          <Button startIcon={<Plus size={16} />} onClick={() => push({ id: `v${Date.now()}`, title: '', thumbnail: '', duration: '', youtube_url: '' })} variant="outlined">Add Video</Button>
                        </Box>
                      )}
                    </FieldArray>
                  </Box>
                )}

                {/* Tab 5: News & Events */}
                {activeTab === 5 && (
                  <Box>
                    <Typography variant="h6" sx={{ mb: 2 }}>Events</Typography>
                    <FieldArray name="events">
                      {({ push, remove, swap }) => (
                        <Box sx={{ mb: 4 }}>
                          {values.events.map((_, index) => (
                            <Paper key={index} sx={{ p: 2, mb: 2, border: `1px solid ${tokens.colors.border}`, display: 'flex', flexDirection: 'column', gap: 2 }}>
                              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <Typography variant="subtitle2" fontWeight="bold">Event #{index + 1}</Typography>
                                <ArrayControls index={index} remove={remove} swap={swap} length={values.events.length} />
                              </Box>
                              <Grid container spacing={2}>
                                <Grid size={{ xs: 12, sm: 6 }}>
                                  <FormField name={`events.${index}.title`} label="Title" />
                                </Grid>
                                <Grid size={{ xs: 12, sm: 6 }}>
                                  <FormField name={`events.${index}.date`} label="Date" />
                                </Grid>
                                <Grid size={{ xs: 12, sm: 6 }}>
                                  <FormField name={`events.${index}.time`} label="Time" />
                                </Grid>
                                <Grid size={{ xs: 12, sm: 6 }}>
                                  <FormField name={`events.${index}.location`} label="Location" />
                                </Grid>
                                <Grid size={{ xs: 12 }}>
                                  <FormField name={`events.${index}.description`} label="Description" multiline rows={2} />
                                </Grid>
                                <Grid size={{ xs: 12 }}>
                                  <FormField name={`events.${index}.image`} label="Image URL" />
                                </Grid>
                              </Grid>
                            </Paper>
                          ))}
                          <Button startIcon={<Plus size={16} />} onClick={() => push({ id: `e${Date.now()}`, title: '', date: '', time: '', location: '', description: '', image: '' })} variant="outlined">Add Event</Button>
                        </Box>
                      )}
                    </FieldArray>

                    <Typography variant="h6" sx={{ mb: 2, mt: 4 }}>News</Typography>
                    <FieldArray name="news">
                      {({ push, remove, swap }) => (
                        <Box sx={{ mb: 4 }}>
                          {values.news.map((_, index) => (
                            <Paper key={index} sx={{ p: 2, mb: 2, border: `1px solid ${tokens.colors.border}`, display: 'flex', flexDirection: 'column', gap: 2 }}>
                              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <Typography variant="subtitle2" fontWeight="bold">News #{index + 1}</Typography>
                                <ArrayControls index={index} remove={remove} swap={swap} length={values.news.length} />
                              </Box>
                              <Grid container spacing={2}>
                                <Grid size={{ xs: 12, sm: 6 }}>
                                  <FormField name={`news.${index}.title`} label="Title" />
                                </Grid>
                                <Grid size={{ xs: 12, sm: 6 }}>
                                  <FormField name={`news.${index}.date`} label="Date" />
                                </Grid>
                                <Grid size={{ xs: 12 }}>
                                  <FormField name={`news.${index}.category`} label="Category" />
                                </Grid>
                                <Grid size={{ xs: 12 }}>
                                  <FormField name={`news.${index}.summary`} label="Summary" multiline rows={2} />
                                </Grid>
                              </Grid>
                            </Paper>
                          ))}
                          <Button startIcon={<Plus size={16} />} onClick={() => push({ id: `n${Date.now()}`, title: '', date: '', category: '', summary: '' })} variant="outlined">Add News</Button>
                        </Box>
                      )}
                    </FieldArray>
                  </Box>
                )}

                {/* Tab 6: Community */}
                {activeTab === 6 && (
                  <Box>
                    <Typography variant="h6" sx={{ mb: 2 }}>Testimonials</Typography>
                    <FieldArray name="testimonials">
                      {({ push, remove, swap }) => (
                        <Box sx={{ mb: 4 }}>
                          {values.testimonials.map((_, index) => (
                            <Paper key={index} sx={{ p: 2, mb: 2, border: `1px solid ${tokens.colors.border}`, display: 'flex', flexDirection: 'column', gap: 2 }}>
                              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <Typography variant="subtitle2" fontWeight="bold">Testimonial #{index + 1}</Typography>
                                <ArrayControls index={index} remove={remove} swap={swap} length={values.testimonials.length} />
                              </Box>
                              <Grid container spacing={2}>
                                <Grid size={{ xs: 12, sm: 4 }}>
                                  <FormField name={`testimonials.${index}.author`} label="Author Name" />
                                </Grid>
                                <Grid size={{ xs: 12, sm: 4 }}>
                                  <FormField name={`testimonials.${index}.relation`} label="Relation (e.g. Parent)" />
                                </Grid>
                                <Grid size={{ xs: 12, sm: 4 }}>
                                  <FormField name={`testimonials.${index}.avatar`} label="Avatar URL" />
                                </Grid>
                                <Grid size={{ xs: 12 }}>
                                  <FormField name={`testimonials.${index}.quote`} label="Quote" multiline rows={3} />
                                </Grid>
                              </Grid>
                            </Paper>
                          ))}
                          <Button startIcon={<Plus size={16} />} onClick={() => push({ id: `t${Date.now()}`, quote: '', author: '', relation: '', avatar: '' })} variant="outlined">Add Testimonial</Button>
                        </Box>
                      )}
                    </FieldArray>

                    <Typography variant="h6" sx={{ mb: 2, mt: 4 }}>Faculty Preview</Typography>
                    <FieldArray name="faculty">
                      {({ push, remove, swap }) => (
                        <Box sx={{ mb: 4 }}>
                          {values.faculty.map((_, index) => (
                            <Paper key={index} sx={{ p: 2, mb: 2, border: `1px solid ${tokens.colors.border}`, display: 'flex', gap: 2, alignItems: 'flex-start' }}>
                              <Box sx={{ flex: 1 }}>
                                <Grid container spacing={2}>
                                  <Grid size={{ xs: 12, sm: 4 }}>
                                    <FormField name={`faculty.${index}.name`} label="Name" />
                                  </Grid>
                                  <Grid size={{ xs: 12, sm: 4 }}>
                                    <FormField name={`faculty.${index}.designation`} label="Designation" />
                                  </Grid>
                                  <Grid size={{ xs: 12, sm: 4 }}>
                                    <FormField name={`faculty.${index}.qualification`} label="Qualification" />
                                  </Grid>
                                </Grid>
                              </Box>
                              <ArrayControls index={index} remove={remove} swap={swap} length={values.faculty.length} />
                            </Paper>
                          ))}
                          <Button startIcon={<Plus size={16} />} onClick={() => push({ name: '', designation: '', qualification: '' })} variant="outlined">Add Faculty</Button>
                        </Box>
                      )}
                    </FieldArray>

                    <Typography variant="h6" sx={{ mb: 2, mt: 4 }}>Admission Process</Typography>
                    <FieldArray name="admission_process">
                      {({ push, remove, swap }) => (
                        <Box sx={{ mb: 4 }}>
                          {values.admission_process.map((_, index) => (
                            <Paper key={index} sx={{ p: 2, mb: 2, border: `1px solid ${tokens.colors.border}`, display: 'flex', flexDirection: 'column', gap: 2 }}>
                              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <Typography variant="subtitle2" fontWeight="bold">Step #{index + 1}</Typography>
                                <ArrayControls index={index} remove={remove} swap={swap} length={values.admission_process.length} />
                              </Box>
                              <Grid container spacing={2}>
                                <Grid size={{ xs: 12, sm: 3 }}>
                                  <FormField name={`admission_process.${index}.step`} label="Step Number" />
                                </Grid>
                                <Grid size={{ xs: 12, sm: 9 }}>
                                  <FormField name={`admission_process.${index}.title`} label="Title" />
                                </Grid>
                                <Grid size={{ xs: 12 }}>
                                  <FormField name={`admission_process.${index}.description`} label="Description" multiline rows={2} />
                                </Grid>
                              </Grid>
                            </Paper>
                          ))}
                          <Button startIcon={<Plus size={16} />} onClick={() => push({ step: `0${values.admission_process.length + 1}`, title: '', description: '' })} variant="outlined">Add Step</Button>
                        </Box>
                      )}
                    </FieldArray>
                  </Box>
                )}

                {/* Tab 7: Contact & Social */}
                {activeTab === 7 && (
                  <Grid container spacing={3}>
                    <Grid size={{ xs: 12 }}><Typography variant="h6">Contact Information</Typography></Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <FormField name="contact.email" label="Contact Email" type="email" />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <FormField name="contact.phone" label="Contact Phone" />
                    </Grid>
                    <Grid size={{ xs: 12 }}>
                      <FormField name="contact.address" label="Address" multiline rows={2} />
                    </Grid>
                    <Grid size={{ xs: 12 }}>
                      <FormField name="contact.google_map_embed" label="Google Maps Embed URL" />
                    </Grid>

                    <Grid size={{ xs: 12 }}><Typography variant="h6" sx={{ mt: 2 }}>Social Media Links</Typography></Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <FormField name="social_media.facebook" label="Facebook URL" placeholder="https://facebook.com/..." />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <FormField name="social_media.twitter" label="Twitter/X URL" placeholder="https://twitter.com/..." />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <FormField name="social_media.instagram" label="Instagram URL" placeholder="https://instagram.com/..." />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <FormField name="social_media.youtube" label="YouTube URL" placeholder="https://youtube.com/..." />
                    </Grid>
                  </Grid>
                )}
              </Box>

              <Box sx={{ p: 3, borderTop: `1px solid ${tokens.colors.border}`, bgcolor: tokens.colors.grey[50], display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="body2" color="text.secondary">
                  Ensure all URLs are valid and necessary fields are filled before saving.
                </Typography>
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <Button 
                    onClick={() => handleFillDemoData(setValues, values)} 
                    variant="outlined" 
                    color="secondary"
                    disabled={isSubmitting}
                  >
                    Load Demo Data
                  </Button>
                  <Button onClick={() => navigate(`/super-admin/schools/${id}`)} variant="outlined" disabled={isSubmitting}>
                    Cancel
                  </Button>
                  <Button type="submit" variant="contained" disabled={isSubmitting} startIcon={<Save size={18} />}>
                    {isSubmitting ? 'Saving Configuration...' : 'Save Configuration'}
                  </Button>
                </Box>
              </Box>
            </Paper>
          </Form>
        )}
      </Formik>
    </Box>
  );
}
